(() => {
  if (customElements.get('celio-predictive-search')) return;
  const historyKey = 'celio_search_history_v1';
  const recentKey = 'celio_search_recent_v1';
  const cache = new Map();
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const config = () => document.querySelector('celio-search-config');
  const permitted = () => {
    try { return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() !== false; } catch { return false; }
  };
  const cookie = key => {
    if (!permitted()) return null;
    try {
      const raw = document.cookie.split('; ').find(part => part.startsWith(`${key}=`));
      return raw ? JSON.parse(decodeURIComponent(raw.slice(key.length + 1))) : null;
    } catch { return null; }
  };
  const setCookie = (key, value) => {
    if (!permitted()) return;
    try {
      const days = Number(config()?.dataset.cookieDays || 30);
      document.cookie = `${key}=${encodeURIComponent(JSON.stringify(value))}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    } catch {}
  };
  const history = () => {
    const data = cookie(historyKey);
    return Array.isArray(data) ? data.filter(term => typeof term === 'string' && term.trim()).slice(0, 8) : [];
  };
  const remember = value => {
    const term = String(value || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    if (!term) return;
    const terms = [term, ...history().filter(item => normalize(item) !== normalize(term))].slice(0, 8);
    while (encodeURIComponent(JSON.stringify(terms)).length > 3000) terms.pop();
    setCookie(historyKey, terms);
  };
  const trackProduct = () => {
    const handle = config()?.dataset.productHandle;
    if (handle && /^[\p{L}\p{N}_-]+$/u.test(handle)) setCookie(recentKey, { handle, viewedAt: Date.now() });
  };
  const recentHandle = () => {
    const saved = cookie(recentKey);
    if (saved && typeof saved.handle === 'string' && /^[\p{L}\p{N}_-]+$/u.test(saved.handle)) return saved.handle;
    if (!permitted()) return '';
    try {
      const legacy = JSON.parse(localStorage.getItem('recently_viewed'));
      if (!Array.isArray(legacy) || !legacy.length) return '';
      const url = new URL(legacy.at(-1).productUrl, location.origin);
      if (url.origin !== location.origin) return '';
      return url.pathname.match(/\/products\/([\p{L}\p{N}_-]+)\/?$/u)?.[1] || '';
    } catch { return ''; }
  };
  const matches = (keywords, query) => {
    const haystack = normalize(keywords).split(' ');
    return normalize(query).split(' ').filter(Boolean).map(token => token.length > 3 ? token.replace(/s$/, '') : token).every(token => haystack.some(word => word.includes(token)));
  };

  class CelioPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      if (!this.input || !this.form) return;
      this.sequence = 0;
      this.isOpen = false;
      const listen = (target, event, action, options = {}) => target?.addEventListener(event, action, { ...options, signal: this.events.signal });
      listen(this.input, 'focus', () => this.open());
      listen(this.input, 'input', () => this.changed());
      listen(this.input, 'change', () => this.updatePlaceholder());
      listen(this.form, 'reset', () => {
        this.input.value = '';
        this.changed();
        this.input.focus();
      });
      listen(this.form, 'submit', event => {
        if (!this.input.value.trim()) { event.preventDefault(); this.open(); return; }
        remember(this.input.value);
      });
      listen(this, 'click', event => this.clicked(event));
      listen(this, 'pointerdown', event => {
        if (!this.panel?.contains(event.target) || !event.target.closest?.('a, button')) return;
        clearTimeout(this.pointerReleaseTimer);
        this.pointerInside = true;
        if (event.pointerType === 'mouse' && event.button === 0) event.preventDefault();
      }, { capture: true });
      listen(document, 'pointerup', () => {
        if (!this.pointerInside) return;
        clearTimeout(this.pointerReleaseTimer);
        this.pointerReleaseTimer = setTimeout(() => {
          this.pointerInside = false;
          if (!this.contains(document.activeElement)) this.close();
        }, 500);
      });
      listen(document, 'pointercancel', () => { this.pointerInside = false; });
      listen(this, 'keydown', event => this.keydown(event));
      listen(this, 'focusout', () => queueMicrotask(() => {
        if (!this.pointerInside && !this.contains(document.activeElement)) this.close();
      }));
      listen(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(); });
      listen(window, 'resize', () => this.position());
      listen(window, 'scroll', () => this.position());
      listen(window.visualViewport, 'resize', () => this.position());
      listen(window.visualViewport, 'scroll', () => this.position());
      listen(window, 'pageshow', () => { trackProduct(); if (this.isOpen) this.refresh(); });
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      listen(this.motionPreference, 'change', () => this.updatePlaceholder());
      listen(document, 'visibilitychange', () => this.updatePlaceholder());
      this.setupPlaceholder();
      this.querySelector('[type="reset"]').hidden = !this.input.value;
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.recentRequest?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.pointerReleaseTimer);
      this.stopPlaceholder();
      this.events = null;
    }

    setupPlaceholder() {
      this.stopPlaceholder();
      this.placeholder = this.querySelector('[data-celio-placeholder]');
      if (!this.placeholder) return;
      const source = config();
      this.placeholderTerms = [...(source?.querySelector('[data-celio-placeholder-terms]')?.content.querySelectorAll('span') || [])].map(term => term.textContent.trim()).filter(Boolean);
      this.placeholderPrefix = source?.dataset.placeholderPrefix || '';
      this.placeholderEnabled = source?.dataset.placeholderEnabled === 'true' && this.placeholderTerms.length > 0;
      this.placeholderInterval = Math.max(1000, Number(source?.dataset.placeholderInterval || 1) * 1000);
      this.placeholderIndex = 0;
      this.placeholder.querySelector('[data-celio-placeholder-prefix]').textContent = this.placeholderPrefix;
      this.placeholderCurrent = this.placeholder.querySelector('[data-celio-placeholder-current]');
      this.placeholderNext = this.placeholder.querySelector('[data-celio-placeholder-next]');
      this.placeholderCurrent.textContent = this.placeholderTerms[0] || '';
      this.placeholderNext.textContent = '';
      this.originalPlaceholder ||= this.input.placeholder;
      this.input.placeholder = this.placeholderTerms.length ? [this.placeholderPrefix, this.placeholderTerms[0]].filter(Boolean).join(' ') : this.originalPlaceholder;
      this.updatePlaceholder();
    }

    stopPlaceholder() {
      clearInterval(this.placeholderTimer);
      this.placeholderTimer = null;
      this.placeholderAnimations?.forEach(animation => animation.cancel());
      this.placeholderAnimations = null;
    }

    updatePlaceholder() {
      if (!this.placeholder) return;
      this.stopPlaceholder();
      const visible = this.placeholderEnabled && this.input.value.length === 0;
      this.placeholder.hidden = !visible;
      this.classList.toggle('celio-search-input--animated', visible);
      if (this.placeholderCurrent) {
        this.placeholderCurrent.textContent = this.placeholderTerms[this.placeholderIndex] || '';
        this.placeholderCurrent.style.transform = 'translateY(0)';
        this.placeholderNext.style.transform = 'translateY(100%)';
      }
      if (!visible || this.placeholderTerms.length < 2 || document.hidden || this.motionPreference?.matches) return;
      this.placeholderTimer = setInterval(() => this.advancePlaceholder(), this.placeholderInterval);
    }

    advancePlaceholder() {
      if (!this.isConnected || this.input.value || document.hidden) { this.updatePlaceholder(); return; }
      const nextIndex = (this.placeholderIndex + 1) % this.placeholderTerms.length;
      this.placeholderNext.textContent = this.placeholderTerms[nextIndex];
      const options = { duration: 300, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'forwards' };
      const animations = [this.placeholderCurrent.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-100%)' }], options), this.placeholderNext.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], options)];
      this.placeholderAnimations = animations;
      Promise.all(animations.map(animation => animation.finished)).then(() => {
        if (this.placeholderAnimations !== animations) return;
        this.placeholderIndex = nextIndex;
        this.placeholderCurrent.textContent = this.placeholderTerms[nextIndex];
        this.placeholderCurrent.style.transform = 'translateY(0)';
        this.placeholderNext.style.transform = 'translateY(100%)';
        animations.forEach(animation => animation.cancel());
        this.placeholderAnimations = null;
      }).catch(() => {});
    }

    mount() {
      if (this.panel) return true;
      const template = config()?.querySelector('[data-celio-panel-template]');
      if (!template) return false;
      this.append(template.content.cloneNode(true));
      this.panel = this.querySelector('.celio-panel');
      this.panel.id = `${this.input.id}-panel`;
      this.panel.setAttribute('role', 'region');
      this.panel.setAttribute('aria-label', this.input.labels?.[0]?.textContent.trim() || this.input.placeholder);
      this.products = this.panel.querySelector('[data-celio-products]');
      this.message = this.panel.querySelector('[data-celio-message]');
      const slots = { quick: '.celio-quick__list', popular: '.celio-popular__list', category: '.celio-categories__list', benefit: '.celio-benefits' };
      this.panel.querySelectorAll('[data-celio-kind]').forEach(block => this.panel.querySelector(slots[block.dataset.celioKind]).append(block));
      this.panel.querySelector('[data-celio-blocks]').remove();
      this.defaults = [...this.panel.querySelectorAll('[data-celio-kind="quick"]')].map(a => a.cloneNode(true));
      this.categoryDefaults = [...this.panel.querySelectorAll('[data-celio-kind="category"]')].map(a => a.cloneNode(true));
      this.collectionHTML = '';
      this.refreshQuick();
      for (const kind of ['popular', 'benefit']) this.panel.querySelector(`[data-celio-zone="${kind}"]`).hidden = !this.panel.querySelector(`[data-celio-kind="${kind}"]`);
      return true;
    }

    refreshQuick() {
      const list = this.panel.querySelector('.celio-quick__list');
      const limit = Number(config()?.dataset.historyLimit || 5);
      const saved = history();
      const defaults = this.defaults.filter(a => !saved.some(term => normalize(term) === normalize(a.dataset.query)));
      list.replaceChildren(...defaults.map(a => a.cloneNode(true)));
      for (const term of saved.reverse()) {
        const link = (this.defaults[0] || this.panel.querySelector('.celio-popular .celio-term'))?.cloneNode(true);
        if (!link) continue;
        link.dataset.celioKind = 'quick';
        link.removeAttribute('data-block-id');
        link.removeAttribute('data-shopify-editor-block');
        link.dataset.query = term;
        link.querySelector('span').textContent = term;
        link.href = this.searchURL(term);
        list.prepend(link);
      }
      [...list.children].slice(limit).forEach(a => a.remove());
      this.panel.querySelector('.celio-quick').hidden = !list.children.length;
    }

    searchURL(query) {
      const url = new URL(this.panel.dataset.searchUrl, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url.href;
    }

    open() {
      if (this.dataset.enabled === 'false' || !this.mount()) return;
      document.querySelectorAll('celio-predictive-search').forEach(other => { if (other !== this) other.close(); });
      this.isOpen = true;
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.refreshQuick();
      this.position();
      this.refresh();
      this.loadRecent();
    }

    close() {
      if (!this.panel) return;
      this.isOpen = false;
      this.removeAttribute('open');
      this.panel.hidden = true;
      this.sequence++;
      clearTimeout(this.timer);
      this.request?.abort();
      this.panel.removeAttribute('aria-busy');
    }

    position() {
      if (!this.isOpen) return;
      const rect = this.form.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewTop = viewport?.offsetTop || 0;
      const viewHeight = viewport?.height || window.innerHeight;
      if (rect.bottom <= 0 || rect.top > viewTop + viewHeight) { this.close(); return; }
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const width = mobile ? document.documentElement.clientWidth : Math.min(parseFloat(this.panel.style.getPropertyValue('--celio-width')) || 920, document.documentElement.clientWidth - 32);
      const left = mobile ? 0 : Math.max(16, Math.min(rect.left, document.documentElement.clientWidth - width - 16));
      const top = Math.max(viewTop, rect.bottom + (mobile ? 8 : 12));
      Object.assign(this.panel.style, { width: `${width}px`, left: `${left}px`, top: `${top}px`, maxHeight: `${Math.max(80, viewHeight + viewTop - top - (mobile ? 0 : 16))}px` });
    }

    changed() {
      this.updatePlaceholder();
      this.sequence++;
      this.request?.abort();
      clearTimeout(this.timer);
      this.querySelector('[type="reset"]').hidden = !this.input.value;
      if (!this.isOpen) { this.open(); return; }
      this.products.replaceChildren();
      this.collectionHTML = '';
      this.filterCategories();
      this.message.textContent = '';
      this.timer = setTimeout(() => this.refresh(), 200);
    }

    filterCategories() {
      const query = this.input.value.trim();
      const signature = query + '\u0000' + (query ? this.collectionHTML : '');
      const list = this.panel.querySelector('.celio-categories__list');
      if (signature !== this.categorySignature) {
        if (!query) {
          list.replaceChildren(...this.categoryDefaults.map(category => category.cloneNode(true)));
        } else {
          const template = document.createElement('template');
          template.innerHTML = this.collectionHTML;
          const collections = [...template.content.querySelectorAll('[data-celio-dynamic-category]')].filter(category => matches(category.dataset.keywords, query)).slice(0, 4);
          list.replaceChildren(...collections);
        }
        this.categorySignature = signature;
      }
      const count = list.querySelectorAll('.celio-category').length;
      this.panel.querySelector('.celio-categories').hidden = count === 0;
      this.panel.classList.toggle('celio-panel--no-categories', !count);
      this.productLimit = count ? 2 : 5;
      this.products.querySelectorAll('[data-celio-product]').forEach((product, index) => { product.hidden = index >= this.productLimit; });
    }

    async refresh() {
      if (!this.isOpen) return;
      this.filterCategories();
      const query = this.input.value.trim() || this.panel.dataset.defaultQuery.trim();
      this.panel.querySelector('[data-celio-query]').textContent = query ? `“${query}”` : '';
      const all = this.panel.querySelector('[data-celio-all]');
      all.href = this.searchURL(query);
      all.hidden = !query;
      if (!query) { this.products.replaceChildren(); this.message.textContent = ''; return; }
      const sequence = ++this.sequence;
      this.request?.abort();
      this.request = new AbortController();
      this.panel.setAttribute('aria-busy', 'true');
      const url = new URL(this.panel.dataset.suggestUrl, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'celio-predictive-results');
      try {
        let payload = cache.get(url.href);
        if (payload === undefined) {
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = doc.querySelector('[data-celio-result-list]');
          if (!results) throw new Error('Search response missing');
          payload = { products: results.innerHTML, collections: doc.querySelector('[data-celio-result-collections]')?.innerHTML || '' };
          cache.set(url.href, payload);
          if (cache.size > 20) cache.delete(cache.keys().next().value);
        }
        if (sequence !== this.sequence || !this.isOpen) return;
        this.products.innerHTML = payload.products;
        this.collectionHTML = payload.collections;
        this.filterCategories();
        const count = Math.min(this.products.querySelectorAll('[data-celio-product]').length, this.productLimit);
        this.message.textContent = count ? this.panel.dataset.count.replace('[count]', count) : this.panel.dataset.empty;
        this.message.classList.toggle('celio-sr-only', count > 0);
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        this.products.replaceChildren();
        this.collectionHTML = '';
        this.filterCategories();
        this.message.classList.remove('celio-sr-only');
        this.message.textContent = this.panel.dataset.error;
      } finally {
        if (sequence === this.sequence) this.panel.removeAttribute('aria-busy');
      }
    }

    async loadRecent() {
      const holder = this.panel.querySelector('[data-celio-recent]');
      const handle = recentHandle();
      if (!handle) { holder.hidden = true; holder.replaceChildren(); return; }
      if (this.recentLoaded === handle && holder.childElementCount) { holder.hidden = false; return; }
      this.recentRequest?.abort();
      this.recentRequest = new AbortController();
      holder.hidden = true;
      try {
        const root = this.panel.dataset.productRoot.replace(/\/?$/, '/');
        const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
        url.searchParams.set('section_id', 'celio-predictive-recent');
        const response = await fetch(url, { signal: this.recentRequest.signal });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const product = doc.querySelector('[data-celio-recent-product]');
        if (!product?.querySelector('[data-celio-product]') || !permitted()) return;
        holder.replaceChildren(product);
        holder.hidden = false;
        this.recentLoaded = handle;
      } catch {}
    }

    clicked(event) {
      this.pointerInside = false;
      if (event.target.closest('[data-celio-close]')) { this.close(); this.input.focus({ preventScroll: true }); this.close(); return; }
      const link = event.target.closest('a');
      if (!link || !this.panel?.contains(link)) return;
      const query = link.dataset.query || new URL(link.href, location.origin).searchParams.get('q') || this.input.value.trim();
      if (query) remember(query);
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || link.target && link.target !== '_self') return;
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(link.href);
    }

    keydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.input.focus({ preventScroll: true });
        this.close();
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      if (!this.isOpen) this.open();
      if (!this.isOpen) return;
      const links = [...this.panel.querySelectorAll('a[href]')].filter(link => link.getClientRects().length && !link.closest('[hidden]'));
      if (!links.length) return;
      event.preventDefault();
      const index = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? (index + 1) % links.length : (index <= 0 ? links.length - 1 : index - 1);
      links[next].focus();
    }
  }

  customElements.define('celio-predictive-search', CelioPredictiveSearch);
  trackProduct();
  const searchPath = config()?.querySelector('[data-celio-panel-template]')?.content.querySelector('.celio-panel')?.dataset.searchUrl;
  if (searchPath && location.pathname === new URL(searchPath, location.origin).pathname) remember(new URL(location.href).searchParams.get('q'));
  document.addEventListener('visitorConsentCollected', () => {
    if (!permitted()) {
      for (const key of [historyKey, recentKey]) document.cookie = `${key}=; Max-Age=0; Path=/; SameSite=Lax`;
    } else trackProduct();
    document.querySelectorAll('celio-predictive-search[open]').forEach(search => { search.refreshQuick(); search.loadRecent(); });
  });
  const preview = event => {
    const source = config();
    const blockId = event.detail?.blockId;
    if (blockId && ![...(source?.querySelector('[data-celio-panel-template]')?.content.querySelectorAll('[data-block-id]') || [])].some(block => block.dataset.blockId === blockId)) return;
    if (!event.target.closest?.('[data-celio-preview]') && !blockId && event.detail?.sectionId !== source?.dataset.sectionId) return;
    const search = [...document.querySelectorAll('celio-predictive-search')].find(element => element.getClientRects().length);
    if (!search) return;
    search.input.focus();
    search.open();
    const block = event.detail?.blockId;
    if (block) [...search.panel.querySelectorAll('[data-block-id]')].find(element => element.dataset.blockId === block)?.scrollIntoView({ block: 'nearest' });
  };
  document.addEventListener('click', event => { if (event.target.closest('[data-celio-preview]')) preview(event); });
  document.addEventListener('shopify:block:select', preview);
  document.addEventListener('shopify:section:select', preview);
  document.addEventListener('shopify:section:load', event => {
    if (!event.target.matches?.('celio-search-config') && !event.target.querySelector('celio-search-config')) return;
    document.querySelectorAll('celio-predictive-search').forEach(search => {
      const wasOpen = search.isOpen;
      search.close();
      search.panel?.remove();
      search.panel = null;
      search.setupPlaceholder();
      if (wasOpen) search.open();
    });
  });
  document.addEventListener('shopify:section:unload', event => {
    if (!event.target.matches?.('celio-search-config') && !event.target.querySelector('celio-search-config')) return;
    document.querySelectorAll('celio-predictive-search').forEach(search => {
      search.close(); search.panel?.remove(); search.panel = null;
      search.stopPlaceholder();
      if (search.placeholder) search.placeholder.hidden = true;
      search.classList.remove('celio-search-input--animated');
      search.input.placeholder = search.originalPlaceholder;
    });
  });
})();
